import React from 'react';
import { Stack, Typography } from '@mui/material';

import useTokenReplacement from '../hooks/ui/useTokenReplacement';

/**
 * LegendItem - Component to display a chart legend element
 *
 * @param {Object} props - Component props
 * @param {string} props.label - Element label (supports tokens)
 * @param {string} [props.description] - Optional description (supports tokens)
 * @param {string} props.color - Indicator circle color
 * @returns {React.ReactElement} Legend element with colored circle and text
 */
const LegendItem = ({ label, description, color }) => {
  const replaceTokens = useTokenReplacement();

  return (
    <Stack
      flexBasis={description ? '50%' : 0}
      flexGrow={1}
      direction="column"
      alignItems={description ? 'flex-start' : 'center'}
      key={label}
      sx={{ minWidth: 0 }}
    >
      <Stack
        spacing={1}
        alignItems="center"
        direction="row"
        sx={{
          textOverflow: 'ellipsis',
          whiteSpace: description ? 'normal' : 'nowrap',
          width: description ? '100%' : '175px',
        }}
      >
        {/* Decorative: the colour is a visual cue for the label read out next to it */}
        <svg width={16} height={16} aria-hidden="true">
          <circle cx="8" cy="8" r="8" fill={color} />
        </svg>
        <Typography
          variant={description ? 'body1' : 'body2'}
          sx={{ '@media print': { fontSize: '0.9rem' } }}
        >
          {replaceTokens(label)}
        </Typography>
      </Stack>

      {description && (
        <Typography
          variant="body2"
          sx={{ '@media print': { fontSize: '0.8rem' } }}
        >
          {replaceTokens(description)}
        </Typography>
      )}
    </Stack>
  );
};

LegendItem.displayName = 'LegendItem';

export default React.memo(LegendItem);
